/**
 * All copy, sections data, and static content for the BoostF landing page.
 * Kept in one place so the site is easy to maintain and localize later.
 */

export const BRAND = {
  name: "BoostF",
  tagline: "eCommerce Launch Marathon",
  logoUrl:
    "https://customer-assets.emergentagent.com/job_750bdf8a-74e1-49a2-8d61-f594d54d5d6d/artifacts/o74gdxzq_Untitled%20design%20%2815%29.png",
  price: 399,
  currency: "INR",
  supportEmail: process.env.REACT_APP_SUPPORT_EMAIL || "boostf.support@gmail.com",
  mentorBioUrl:
    process.env.REACT_APP_MENTOR_BIO_URL || "https://winsaa.org/wiki/Mohammad_Asim",
};

export const NAV_LINKS = [
  { label: "Home", href: "#top" },
  { label: "Roadmap", href: "#roadmap" },
  { label: "Mentor", href: "#mentor" },
  { label: "Results", href: "#results" },
  { label: "FAQ", href: "#faq" },
];

export const HERO = {
  chip: "eCommerce Launch Marathon · 2026 Cohort",
  headlineLines: ["Build.", "Launch.", "Scale."],
  headlineSuffix: "Your eCommerce Business.",
  subheading:
    "Join the BoostF eCommerce Launch Marathon and learn everything from product research and Shopify setup to supplier connections, Meta Ads, AI tools and getting your first sale.",
  priceBadge: "₹399 Only",
};

export const WHY_BOOSTF = [
  {
    title: "Learn By Doing",
    kicker: "01 / Practical",
    body:
      "Everything is practical — no dense theory. You build, launch, and fix things live, not from slides.",
  },
  {
    title: "Beginner Friendly",
    kicker: "02 / Accessible",
    body:
      "Designed for complete beginners. Every step is broken down, so absolutely no prior experience is needed.",
  },
  {
    title: "Business Focused",
    kicker: "03 / Outcome",
    body:
      "Every module is built around one goal — launching a real business you can proudly point to.",
  },
];

export const MENTOR = {
  name: "Mohammad Asim",
  roles: ["Entrepreneur", "Business Mentor", "eCommerce Strategist", "Community Builder"],
  bio: [
    "Mohammad Asim is an entrepreneur and business mentor focused on helping first-time founders build sustainable online businesses.",
    "His work sits at the intersection of practical eCommerce, product-market thinking and community building — with a strong belief that the fastest way to learn is by shipping something real.",
    "Through BoostF, he leads the eCommerce Launch Marathon: a compact, high-intent program that walks beginners from an empty screen to a live Shopify store with real orders.",
  ],
};

export const RESULTS_STATS = [
  { label: "Revenue Growth", value: 340, suffix: "%", accent: "emerald" },
  { label: "Store Launches", value: 1200, suffix: "+", accent: "lime" },
  { label: "Real Projects", value: 87, suffix: "+", accent: "emerald" },
  { label: "Business Growth", value: 4.6, suffix: "x", accent: "lime", decimals: 1 },
];

export const ROADMAP = [
  {
    phase: "01",
    title: "Business Foundation",
    summary:
      "Set the mindset, decide your niche and validate demand before spending a rupee.",
    items: ["Mindset for founders", "Winning product research", "Market validation", "Brand building basics"],
  },
  {
    phase: "02",
    title: "Shopify Store Setup",
    summary:
      "Build a store that looks premium and actually converts — theme, pages, and payments.",
    items: [
      "Premium theme setup",
      "Store configuration",
      "High-converting product pages",
      "Conversion optimization",
      "Payment gateway setup",
      "Shipping setup",
    ],
  },
  {
    phase: "03",
    title: "Supplier Connection",
    summary: "Meet the suppliers, sort fulfillment and get products moving.",
    items: ["Supplier sourcing", "Fulfillment workflow", "Quality checks", "Cost & margin planning"],
  },
  {
    phase: "04",
    title: "Meta Ads & Creatives",
    summary: "Turn a store into a business with cold traffic that pays for itself.",
    items: ["Creative strategy", "Campaign structure", "Scaling frameworks", "Reading the numbers"],
  },
  {
    phase: "05",
    title: "AI Tools & Automation",
    summary: "Leverage modern AI stacks so a one-person store runs like a team of five.",
    items: ["AI prompt library", "Creative automation", "Support automation", "Workflow templates"],
  },
  {
    phase: "06",
    title: "Getting First Sale",
    summary: "Ship the launch, get the first order and open the loop for long-term growth.",
    items: [
      "Launch checklist",
      "First-sale playbook",
      "Customer psychology",
      "Long-term business growth",
    ],
  },
];

export const WHAT_YOU_LEARN = [
  "Business Basics",
  "Winning Product Research",
  "Brand Building",
  "Shopify Setup",
  "Theme Customization",
  "Product Page Design",
  "Payment Gateway Setup",
  "Shipping Setup",
  "Supplier Sourcing",
  "Order Fulfillment",
  "Meta Ads",
  "Creative Design",
  "Scaling",
  "AI Tools",
  "Automation",
  "Customer Psychology",
  "Conversion Optimization",
  "Getting First Sale",
  "Business Growth",
];

export const BONUSES = [
  { title: "Supplier Resources", body: "A hand-picked directory of vetted Indian & global suppliers." },
  { title: "Premium Templates", body: "Ready-to-use store, ad and email templates you can plug in on day one." },
  { title: "Store Checklist", body: "A launch-day checklist so nothing critical slips through." },
  { title: "Marketing Framework", body: "The exact framework used to structure campaigns and creatives." },
  { title: "AI Prompt Library", body: "Prompts for products, copy, creatives, support and everything in between." },
  { title: "Private Community", body: "A tight WhatsApp community of founders shipping alongside you." },
  { title: "Future Updates", body: "Free lifetime access to every future update to the marathon." },
];

export const AUDIENCE = [
  { title: "Students", body: "Build a real business alongside your studies." },
  { title: "Freelancers", body: "Turn your service income into an asset you own." },
  { title: "Working Professionals", body: "A side business that could become your main one." },
  { title: "Business Owners", body: "Add a modern eCommerce arm to what you already run." },
  { title: "Complete Beginners", body: "No experience needed — start from zero with confidence." },
  { title: "eCommerce Curious", body: "You've been watching from the outside. Time to actually build." },
];

export const POST_REG_STEPS = [
  { step: "01", label: "Receive Payment Confirmation" },
  { step: "02", label: "Join WhatsApp Community" },
  { step: "03", label: "Receive Preparation Resources" },
  { step: "04", label: "Receive Official Marathon Date" },
  { step: "05", label: "Receive Joining Instructions" },
];

export const JOURNEY = [
  "Reserve Seat",
  "Complete Payment",
  "Receive Confirmation Email",
  "Join WhatsApp Community",
  "Marathon Starts",
  "Launch Store",
  "Get First Sale",
];

export const RESERVE_INCLUDES = [
  "Complete Marathon",
  "Full Roadmap",
  "Shopify Training",
  "Supplier Connections",
  "Meta Ads",
  "AI Tools",
  "Premium Templates",
  "Resources & Checklists",
  "Private Community",
];

export const RESERVE_TRUST = [
  "Secure Payment",
  "Instant Confirmation",
  "Private WhatsApp Community",
  "Confirmation Email",
];

export const FAQS = [
  {
    q: "Do I need any prior experience?",
    a: "None at all. The marathon is designed for complete beginners. Every step is broken down, and the community is there to help.",
  },
  {
    q: "Do I need a laptop?",
    a: "A laptop is strongly recommended for Shopify setup and Meta Ads. You can attend live sessions on a phone, but you'll want a laptop to build alongside us.",
  },
  {
    q: "Will beginners actually understand this?",
    a: "Yes. The entire marathon is built around a beginner-first path — no jargon, no assumed knowledge, just clear steps you can implement.",
  },
  {
    q: "How will I receive updates and joining instructions?",
    a: "Right after your ₹399 registration you'll receive a confirmation email and get access to the private WhatsApp Community, where the schedule and joining instructions are shared.",
  },
  {
    q: "How do I join the WhatsApp Community?",
    a: "Once you complete your registration, the private WhatsApp invite is shared with you via email and on the confirmation screen.",
  },
  {
    q: "Will recordings be available?",
    a: "Yes. Recordings and preparation resources will be shared inside the private community so you can catch up if you miss any live session.",
  },
];
